import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Dragging something onto something else.
 *
 * Pointer events rather than the HTML5 drag API. That API drags a translucent
 * ghost rather than the element, gives no usable coordinates during the drag
 * in some browsers, and does not fire at all on a touchscreen — and this
 * dashboard is used on a classroom tablet as well as a laptop.
 *
 * Drop targets register their own element rather than being found with
 * `elementFromPoint`, because the thing being dragged sits under the pointer
 * the whole time and would be the thing found.
 */
export interface DragOnto<T> {
  /** What is being dragged, once the gesture has passed the threshold. */
  item: T | null;
  at: { x: number; y: number };
  /** The id of the target the pointer is over. */
  over: string | null;
  begin: (item: T, e: React.PointerEvent) => void;
  registerTarget: (id: string, el: HTMLElement | null) => void;
  /**
   * True while a drag is in flight.
   *
   * A press that turns into a drag must not also count as a click, or letting
   * go over a job card opens the child's history at the same moment.
   */
  moved: React.MutableRefObject<boolean>;
}

/** How far the pointer must travel before a press becomes a drag. */
const THRESHOLD = 6;

export function useDragOnto<T>(onDrop: (item: T, targetId: string) => void): DragOnto<T> {
  const [item, setItem] = useState<T | null>(null);
  const [at, setAt] = useState({ x: 0, y: 0 });
  const [over, setOver] = useState<string | null>(null);

  const targets = useRef(new Map<string, HTMLElement>());
  const start = useRef<{
    x: number;
    y: number;
    item: T;
    /** Kept so the pointer can be captured *later* — see `begin`. */
    el: HTMLElement;
    pointerId: number;
  } | null>(null);
  const moved = useRef(false);

  const registerTarget = useCallback((id: string, el: HTMLElement | null) => {
    if (el) targets.current.set(id, el);
    else targets.current.delete(id);
  }, []);

  /** Which target the pointer is inside, by rectangle. */
  const targetAt = useCallback((x: number, y: number) => {
    for (const [id, el] of targets.current) {
      const box = el.getBoundingClientRect();
      if (x >= box.left && x <= box.right && y >= box.top && y <= box.bottom) return id;
    }
    return null;
  }, []);

  const begin = useCallback((next: T, e: React.PointerEvent) => {
    // The primary button only, and never a right-click.
    if (e.button !== 0) return;

    /*
     * A press on a control inside the draggable thing is not a drag.
     *
     * The row that can be dragged also carries a "take them off" button, and
     * a press that lands on that button must stay a press.
     */
    if ((e.target as HTMLElement).closest('[data-no-drag]')) return;

    start.current = {
      x: e.clientX,
      y: e.clientY,
      item: next,
      el: e.currentTarget as HTMLElement,
      pointerId: e.pointerId,
    };
    moved.current = false;

    /*
     * The pointer is deliberately *not* captured here.
     *
     * Capturing on pointerdown sends the whole gesture to the capturing
     * element — including the click at the end of it. So the ✕ inside a
     * draggable row never heard its own click: the row had already claimed
     * it, and the button looked broken. Capture happens once the pointer has
     * actually travelled far enough to be a drag, by which point there is no
     * click left to steal.
     */
  }, []);

  /*
   * Dragging across a page of text selects it, which leaves everything looking
   * highlighted after the drop and swallows the next click.
   */
  useEffect(() => {
    if (!item) return;
    document.body.classList.add('select-none');
    return () => document.body.classList.remove('select-none');
  }, [item]);

  useEffect(() => {
    function move(e: PointerEvent) {
      const from = start.current;
      if (!from) return;

      if (!moved.current && Math.hypot(e.clientX - from.x, e.clientY - from.y) < THRESHOLD) return;

      /*
       * Now it is a drag, so take the pointer.
       *
       * This is what keeps a finger dragging across a tablet working when it
       * crosses another element, and what stops the browser deciding halfway
       * through that it was a scroll after all.
       */
      if (!moved.current) {
        try {
          from.el.setPointerCapture(from.pointerId);
        } catch {
          // The element can be gone if a live update re-rendered the list
          // mid-gesture. The window listeners carry on regardless.
        }
      }

      moved.current = true;
      setItem(from.item);
      setAt({ x: e.clientX, y: e.clientY });
      setOver(targetAt(e.clientX, e.clientY));
    }

    function up(e: PointerEvent) {
      const from = start.current;
      start.current = null;

      if (from && moved.current) {
        const target = targetAt(e.clientX, e.clientY);
        if (target) onDrop(from.item, target);
      }

      setItem(null);
      setOver(null);
    }

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);

    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [onDrop, targetAt]);

  return { item, at, over, begin, registerTarget, moved };
}
